import { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Button, Card, CardContent, Grid, Chip, Alert, Skeleton,
  Tabs, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Select, MenuItem,
} from '@mui/material';
import { FileText, Download, Eye, RefreshCw } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { invoiceApi, HotelInvoice } from '../../services/hotelApi';

const STATUS_COLORS: Record<string, string> = { draft: '#9e9e9e', issued: '#2196f3', paid: '#4caf50', cancelled: '#f44336' };

const Invoices = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const isFr = language === 'fr';
  const currency = user?.company?.signCurrency || '$';

  const [invoices, setInvoices] = useState<HotelInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabIdx, setTabIdx] = useState(0);

  // Detail dialog
  const [selected, setSelected] = useState<HotelInvoice | null>(null);
  const [openDetail, setOpenDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const t = isFr ? {
    title: 'Factures', subtitle: 'Gérer les factures de l\'hôtel',
    all: 'Toutes', draft: 'Brouillon', issued: 'Émise', paid: 'Payée', cancelled: 'Annulée',
    invoiceNumber: 'N° Facture', client: 'Client', room: 'Chambre', roomType: 'Type',
    checkIn: 'Arrivée', checkOut: 'Départ', duration: 'Durée', amount: 'Montant',
    rate: 'Tarif', status: 'Statut', date: 'Date', actions: 'Actions',
    noInvoices: 'Aucune facture', detail: 'Détail de la facture',
    downloadPdf: 'Télécharger PDF', changeStatus: 'Changer le statut',
    close: 'Fermer', days: 'jours', hours: 'heures',
    errorLoading: 'Erreur lors du chargement',
    refresh: 'Actualiser', cardNumber: 'N° Carte',
  } : {
    title: 'Invoices', subtitle: 'Manage hotel invoices',
    all: 'All', draft: 'Draft', issued: 'Issued', paid: 'Paid', cancelled: 'Cancelled',
    invoiceNumber: 'Invoice #', client: 'Client', room: 'Room', roomType: 'Type',
    checkIn: 'Check-In', checkOut: 'Check-Out', duration: 'Duration', amount: 'Amount',
    rate: 'Rate', status: 'Status', date: 'Date', actions: 'Actions',
    noInvoices: 'No invoices', detail: 'Invoice Detail',
    downloadPdf: 'Download PDF', changeStatus: 'Change Status',
    close: 'Close', days: 'days', hours: 'hours',
    errorLoading: 'Error loading data',
    refresh: 'Refresh', cardNumber: 'Card Number',
  };

  const statusFilters = ['', 'draft', 'issued', 'paid', 'cancelled'];
  const statusLabels = [t.all, t.draft, t.issued, t.paid, t.cancelled];

  useEffect(() => { loadInvoices(); }, []);

  const loadInvoices = async () => {
    try {
      setLoading(true); setError('');
      const data = await invoiceApi.getAll();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError(err.message || t.errorLoading);
    } finally { setLoading(false); }
  };

  const filterStatus = statusFilters[tabIdx];
  const filtered = filterStatus ? invoices.filter(inv => inv.status === filterStatus) : invoices;
  const tabCounts = statusFilters.map(s => s ? invoices.filter(inv => inv.status === s).length : invoices.length);

  const fmtDate = (d?: string) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString(isFr ? 'fr-FR' : 'en-US', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const fmtDuration = (dur?: { days: number; hours: number }) => {
    if (!dur) return '-';
    const parts: string[] = [];
    if (dur.days > 0) parts.push(`${dur.days} ${t.days}`);
    if (dur.hours > 0) parts.push(`${dur.hours} ${t.hours}`);
    return parts.join(' ') || '-';
  };

  const handleDownloadPdf = async (inv: HotelInvoice) => {
    try {
      setActionLoading(true);
      const blob = await invoiceApi.getPdf(inv._id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${inv.invoiceNumber}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message);
    } finally { setActionLoading(false); }
  };

  const handleChangeStatus = async (inv: HotelInvoice, newStatus: string) => {
    try {
      setActionLoading(true);
      await invoiceApi.updateStatus(inv._id, newStatus);
      await loadInvoices();
      setOpenDetail(false);
    } catch (err: any) {
      setError(err.message);
    } finally { setActionLoading(false); }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#0D3C4C' }}>{t.title}</Typography>
          <Typography variant="body1" color="text.secondary">{t.subtitle}</Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshCw size={16} />} onClick={loadInvoices}
          sx={{ textTransform: 'none', borderColor: '#0D3C4C', color: '#0D3C4C' }}>{t.refresh}</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Tabs value={tabIdx} onChange={(_, v) => setTabIdx(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        {statusLabels.map((label, i) => (
          <Tab key={i} label={`${label} (${tabCounts[i]})`} sx={{ textTransform: 'none' }} />
        ))}
      </Tabs>

      {loading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 3 }).map((_, i) => <Grid item xs={12} key={i}><Skeleton variant="rounded" height={60} /></Grid>)}
        </Grid>
      ) : filtered.length === 0 ? (
        <Alert severity="info">{t.noInvoices}</Alert>
      ) : (
        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t.invoiceNumber}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.client}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.room}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.duration}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t.amount}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.status}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.date}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t.actions}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(inv => (
                  <TableRow key={inv._id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{inv.invoiceNumber}</TableCell>
                    <TableCell>{inv.clientName}</TableCell>
                    <TableCell>{inv.roomNumber} {inv.roomType ? `• ${inv.roomType}` : ''}</TableCell>
                    <TableCell>{fmtDuration(inv.duration)}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{(inv.totalAmount || 0).toLocaleString()} {currency}</TableCell>
                    <TableCell>
                      <Chip label={statusLabels[statusFilters.indexOf(inv.status)] || inv.status} size="small"
                        sx={{ backgroundColor: `${STATUS_COLORS[inv.status] || '#9e9e9e'}15`, color: STATUS_COLORS[inv.status] || '#9e9e9e', fontWeight: 600, fontSize: '0.7rem' }} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(inv.createdAt)}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => { setSelected(inv); setOpenDetail(true); }}><Eye size={16} /></IconButton>
                      <IconButton size="small" onClick={() => handleDownloadPdf(inv)} disabled={actionLoading}><Download size={16} /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={openDetail} onClose={() => setOpenDetail(false)} maxWidth="sm" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ fontWeight: 600 }}>{t.detail} — {selected.invoiceNumber}</DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2}>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.client}</Typography><Typography variant="body2" sx={{ fontWeight: 500 }}>{selected.clientName}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.room}</Typography><Typography variant="body2">{selected.roomNumber} • {selected.roomType}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.checkIn}</Typography><Typography variant="body2">{fmtDate(selected.checkIn)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.checkOut}</Typography><Typography variant="body2">{fmtDate(selected.checkOut)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.duration}</Typography><Typography variant="body2">{fmtDuration(selected.duration)}</Typography></Grid>
                <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.rate}</Typography><Typography variant="body2">{selected.rateApplied} — {selected.rateDetails}</Typography></Grid>
                {selected.cardNumber && <Grid item xs={6}><Typography variant="caption" color="text.secondary">{t.cardNumber}</Typography><Typography variant="body2">{selected.cardNumber}</Typography></Grid>}
                <Grid item xs={12}>
                  <Box sx={{ backgroundColor: '#f5f5f5', p: 2, borderRadius: 2, textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#0D3C4C' }}>{(selected.totalAmount || 0).toLocaleString()} {currency}</Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{t.changeStatus}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {['draft', 'issued', 'paid', 'cancelled'].filter(s => s !== selected.status).map(s => (
                      <Button key={s} size="small" variant="outlined"
                        onClick={() => handleChangeStatus(selected, s)} disabled={actionLoading}
                        sx={{ textTransform: 'none', borderColor: STATUS_COLORS[s], color: STATUS_COLORS[s] }}>
                        {statusLabels[statusFilters.indexOf(s)]}
                      </Button>
                    ))}
                  </Box>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => handleDownloadPdf(selected)} startIcon={<Download size={16} />} disabled={actionLoading}
                sx={{ textTransform: 'none' }}>{t.downloadPdf}</Button>
              <Button onClick={() => setOpenDetail(false)} sx={{ textTransform: 'none' }}>{t.close}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default Invoices;
